import * as React from "react";
import LocalStorageService from "../../../auth/local-storage-service";
import { UserService } from "../../../services/user-service";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { resolveLoadingPresentation } from "../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaSkeletonDetail, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import { ChangePwdModal } from "./change-pwd-modal";
import { EMemberInfoType } from "./member-info-modal";
import { ProfileUserInfo } from "./profile-user-info";
import { ProfileUserProjects } from "./profile-user-projects";
import { ProfileUserUpdate } from "./profile-user-update";

export interface IMyProfileState {
  mode: Mode;
  isChangePasswordModalActive: boolean;
  /**
   * Where the form's submit stands, which the dialog has to know about because the buttons
   * that commit it live in the modal's own footer slot rather than inside the form. Owned by
   * `SaveStatus`, never set directly.
   */
  saveStatus: KuikaButtonStatus;
  /**
   * The refresh behind the dialog, which is NOT the same thing as having nothing to show.
   * The body paints from the context, so this only blocks when the context is empty.
   */
  isRefreshing: boolean;
}

export interface IMyProfileProps {
  closeModal: any;
  showModal: boolean;
}

enum Mode {
  Edit,
  View
}

/** The edit form's `id`, so the footer's Update can submit it from outside the `<form>`. */
const MY_PROFILE_FORM_ID: string = "my-profile-form";

class MyProfileModal extends React.PureComponent<
  IMyProfileProps & IWithHistoryProp & IWithContextProp,
  IMyProfileState
> {
  /**
   * Update's state machine: the ✓ is held on the button, then the dialog closes — the profile
   * it saved is what the header avatar and the account menu show once it has gone.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IMyProfileProps & IWithHistoryProp & IWithContextProp) {
    super(props);
    this.state = {
      mode: Mode.View,
      isChangePasswordModalActive: false,
      saveStatus: "idle",
      isRefreshing: false
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount = (): void => {
    // Not awaited, and nothing waits on it: the dialog already has a user to draw.
    void this.updateUserInfo();
  };

  onMenuClick = () => {
    this.setState({ mode: Mode.Edit });
  };

  openChangePwdModal = () => {
    const isAutheticatedWithCustomProvider = LocalStorageService.getAuthenticatedWithCustomProvider();

    if (isAutheticatedWithCustomProvider) {
      KMainFunctions.displayErrorNotification(
        "Password change is not possible due to logging in with another provider"
      );
      return;
    }
    this.setState({ isChangePasswordModalActive: true });
  };

  closeChangePwdModal = () => {
    this.setState({ isChangePasswordModalActive: false });
  };

  /**
   * Re-reads the account behind the dialog. It is a refresh, not a load: every field this
   * dialog shows is already on `context.userInfo` — the header avatar and the account menu
   * draw from it — so waiting for the round trip before painting anything cost a full
   * request on every open and showed a spinner over content that was ready.
   */
  updateUserInfo = async (): Promise<void> => {
    this.setState({ isRefreshing: true });
    try {
      const res = await UserService.GetUserInfoForce();
      if (res) {
        this.props.context?.setUserInfo(res);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      KMainFunctions.displayErrorNotification("An error occurred while updating user");
    } finally {
      this.setState({ isRefreshing: false });
    }
  };

  closeProfileModal = async () => {
    this.props.navigator.navigate({
      pathname: this.props.navigator.location.pathname,
      search: "?dataChanged=true"
    });
    this.props.closeModal(false);
  };

  openProject = (screenId: string): void => {
    this.props.closeModal(false);
    this.props.navigator.gotoScreen(screenId);
  };

  handleSubmitStart = (): boolean => {
    if (!this.saveStatus.canStart) return false;
    this.saveStatus.start();
    return true;
  };

  handleSubmitSuccess = (): void => {
    this.saveStatus.succeed((): void => void this.closeProfileModal());
  };

  handleSubmitFailure = (): void => {
    this.saveStatus.fail();
  };

  /**
   * The decisions for the edit form, in the dialog's footer rather than at the end of the
   * body. `submitForm` is how they reach it: the slot is a sibling of the body, so the
   * commit button is outside the `<form>` and the HTML `form` attribute associates them.
   *
   * Reading mode has nothing to commit, so it is drawn no footer at all.
   *
   * No Delete account: nothing is wired behind it. `DeleteMyAccount` collects a reason and
   * sends none — there is no self-service deletion endpoint — so the dialog offered an
   * irreversible-looking action that silently did nothing. It is not offered until it does
   * something; the component is still there to mount when it does.
   */
  renderEditingFooter = (canGoBack: boolean): React.ReactNode => (
    <KModalFooter
      onCancel={canGoBack ? () => this.setState({ mode: Mode.View }) : undefined}
      cancelLabel="Back"
      cancelTestId="myProfileModalBackButton"
      submitLabel="Update"
      submitTestId="myProfileUpdateButton"
      submitForm={MY_PROFILE_FORM_ID}
      status={this.state.saveStatus}
    />
  );

  render() {
    const userInfo = this.props.context?.userInfo;
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    // Without workspace authorization there are no project roles to list, so the form is
    // the only thing this dialog can show.
    const isEditing: boolean = !isDomainAuthorizationEnabled || this.state.mode === Mode.Edit;
    // The body paints from the context, so the refresh behind the dialog shows a placeholder only
    // when the context has no user to draw — never over a populated dialog.
    const isPlaceholder: boolean = resolveLoadingPresentation(this.state.isRefreshing, !!userInfo?.id) === "skeleton";
    return (
      <>
        <ChangePwdModal showModal={this.state.isChangePasswordModalActive} closeModal={this.closeChangePwdModal} />
        <KModal
          className="kuika_profile__modal"
          header={<span data-testid="myProfileModalTitle">My profile</span>}
          closeTestId="closeMyProfileButton"
          showModal={this.props.showModal}
          closeModal={this.closeProfileModal}
          width={720}
          footer={isEditing && !isPlaceholder ? this.renderEditingFooter(isDomainAuthorizationEnabled) : undefined}
        >
          {isPlaceholder && (
            <KuikaSkeletonRegion label="Loading profile">
              <KuikaSkeletonDetail />
            </KuikaSkeletonRegion>
          )}
          {/* Two shapes, not two layouts of one shape: reading is the rail beside the
              app list, editing is the form with its identity header. Editing used to
              keep the rail, which then held three lines of text beside a 488px form —
              and with workspace authorization off it held nothing at all. */}
          {!isPlaceholder && (
            <div className={`kuika_profile${isEditing ? " kuika_profile--editing" : ""}`}>
              {isEditing ? (
                <ProfileUserUpdate
                  formId={MY_PROFILE_FORM_ID}
                  profileInfo={userInfo}
                  openChangePwdModal={this.openChangePwdModal}
                  updateUserInfo={this.updateUserInfo}
                  onSubmitStart={this.handleSubmitStart}
                  onSubmitSuccess={this.handleSubmitSuccess}
                  onSubmitFailure={this.handleSubmitFailure}
                />
              ) : (
                <div className="kuika_profile__split">
                  <ProfileUserInfo
                    userInfo={userInfo}
                    onMenuClick={this.onMenuClick}
                    type={EMemberInfoType.OWNER}
                    emailTestId="myProfileModalEmail"
                  />
                  <div className="kuika_profile__pane">
                    <ProfileUserProjects
                      profileInfo={userInfo}
                      type={EMemberInfoType.OWNER}
                      enrichWithMyProjects
                      onOpenProject={this.openProject}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </KModal>
      </>
    );
  }
}

const myProfileModal = withContext<IMyProfileProps>(
  withHistory<IMyProfileProps & IWithHistoryProp & IWithContextProp>(MyProfileModal)
);
export { myProfileModal as MyProfileModal };
